import { LANG } from "@/configs";
import cookie from "js-cookie";

const DEFAULT_AUTH_LOCALE = "en";
const supportedAuthLocales = new Set(["en", "ko"]);

function normalizeLocale(value?: string | null): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  return trimmed.split("-")[0]?.toLowerCase() || undefined;
}

function resolveSupportedLocale(candidate?: string | null): string | undefined {
  const normalized = normalizeLocale(candidate);
  if (!normalized || !supportedAuthLocales.has(normalized)) {
    return undefined;
  }

  return normalized;
}

export function getStoredAuthLocale(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  return resolveSupportedLocale(cookie.get(LANG));
}

export function setStoredAuthLocale(locale?: string | null): void {
  const resolvedLocale = resolveSupportedLocale(locale);
  if (typeof window === "undefined" || !resolvedLocale) {
    return;
  }

  cookie.set(LANG, resolvedLocale, { expires: 365, path: "/" });
}

export function getPreferredNavigationLocale(
  routerLocale?: string | null,
  defaultLocale?: string | null,
  userLocale?: string | null
): string {
  const resolvedRouterLocale = resolveSupportedLocale(routerLocale);
  const resolvedDefaultLocale = resolveSupportedLocale(defaultLocale) || DEFAULT_AUTH_LOCALE;

  if (resolvedRouterLocale && resolvedRouterLocale !== resolvedDefaultLocale) {
    return resolvedRouterLocale;
  }

  return resolveSupportedLocale(userLocale) || getStoredAuthLocale() || resolvedRouterLocale || resolvedDefaultLocale;
}

export function getPreferredAuthLocale(routerLocale?: string | null): string {
  const routerResolved = resolveSupportedLocale(routerLocale);
  if (routerResolved) {
    return routerResolved;
  }

  const storedLocale = getStoredAuthLocale();
  if (storedLocale) {
    return storedLocale;
  }

  if (typeof window !== "undefined") {
    const browserCandidates = [...(window.navigator.languages || []), window.navigator.language];
    for (const candidate of browserCandidates) {
      const resolved = resolveSupportedLocale(candidate);
      if (resolved) {
        return resolved;
      }
    }
  }

  return DEFAULT_AUTH_LOCALE;
}

export function withLocalePath(path: string, locale?: string | null, defaultLocale?: string | null): string {
  if (!path.startsWith("/")) {
    return path;
  }

  const resolvedLocale = resolveSupportedLocale(locale);
  const resolvedDefaultLocale = resolveSupportedLocale(defaultLocale) || DEFAULT_AUTH_LOCALE;
  if (!resolvedLocale || resolvedLocale === resolvedDefaultLocale) {
    return path;
  }

  const parsedPath = new URL(path, "http://localhost");
  const localePrefix = `/${resolvedLocale}`;
  if (parsedPath.pathname === localePrefix || parsedPath.pathname.startsWith(`${localePrefix}/`)) {
    return path;
  }

  const pathname = parsedPath.pathname === "/" ? "" : parsedPath.pathname;
  return `${localePrefix}${pathname}${parsedPath.search}${parsedPath.hash}`;
}

export function withAcceptLanguageHeader(headers?: HeadersInit, routerLocale?: string | null): Headers {
  const normalizedHeaders = new Headers(headers);
  if (!normalizedHeaders.has("Accept-Language")) {
    normalizedHeaders.set("Accept-Language", getPreferredAuthLocale(routerLocale));
  }

  return normalizedHeaders;
}
